import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Sparkles, RefreshCw, CheckCircle, AlertCircle, FileText, Wand2, Save } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Header from '../../components/ui/Header';
import buyingGuideService from '../../services/buyingGuideService';
import { generateBuyingGuide } from './services/aiBuyingGuideService';

const AIBuyingGuideGenerator = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [formData, setFormData] = useState({
    title: '',
    category: 'home-size',
    targetAudience: '',
    focusPoints: '',
    priceRange: '',
    seasonalRelevance: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin-login');
    }
  }, [user, authLoading, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      if (!formData?.title || !formData?.targetAudience) {
        setError('Please fill in title and target audience');
        return;
      }

      const content = await generateBuyingGuide(formData);
      setGeneratedContent(content);
      setSuccess('Buying guide generated successfully!');
    } catch (err) {
      console.error('Error generating guide:', err);
      setError(err?.message || 'Failed to generate buying guide');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!generatedContent) {
        setError('No content to save');
        return;
      }

      const guideData = {
        title: formData?.title,
        category: formData?.category,
        targetAudience: formData?.targetAudience,
        content: generatedContent?.content,
        summary: generatedContent?.summary,
        keyPoints: generatedContent?.keyPoints,
        recommendedProducts: generatedContent?.recommendedProducts || [],
        seoTitle: generatedContent?.seoTitle,
        seoDescription: generatedContent?.seoDescription,
        seoKeywords: generatedContent?.seoKeywords,
        generatedByAi: true,
        aiPrompt: JSON.stringify(formData),
        readTimeMinutes: generatedContent?.readTimeMinutes,
        seasonalRelevance: formData?.seasonalRelevance ? [formData?.seasonalRelevance] : [],
        isPublished: false
      };

      await buyingGuideService?.createGuide(guideData);
      setSuccess('Buying guide saved successfully!');
      
      setTimeout(() => {
        navigate('/admin/buying-guides');
      }, 2000);
    } catch (err) {
      console.error('Error saving guide:', err);
      setError(err?.message || 'Failed to save buying guide');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: '',
      category: 'home-size',
      targetAudience: '',
      focusPoints: '',
      priceRange: '',
      seasonalRelevance: ''
    });
    setGeneratedContent(null);
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
              <Wand2 size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-text-primary">AI Buying Guide Generator</h1>
              <p className="text-text-secondary">Create comprehensive buying guides with AI-powered content generation</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-green-800">{success}</p>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="bg-white rounded-xl shadow-subtle border border-border p-6">
            <h2 className="text-xl font-semibold text-text-primary mb-6 flex items-center gap-2">
              <FileText size={20} />
              Guide Parameters
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Guide Title *
                </label>
                <Input
                  name="title"
                  value={formData?.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Complete Guide to Pet-Friendly Vacuums"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Category *
                </label>
                <Select
                  name="category"
                  value={formData?.category}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="home-size">By Home Size</option>
                  <option value="floor-type">By Floor Type</option>
                  <option value="lifestyle">By Lifestyle Needs</option>
                  <option value="budget">By Budget Range</option>
                  <option value="seasonal">Seasonal Guides</option>
                  <option value="technology">By Technology</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Target Audience *
                </label>
                <Input
                  name="targetAudience"
                  value={formData?.targetAudience}
                  onChange={handleInputChange}
                  placeholder="e.g., Pet owners with multiple cats and dogs"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Focus Points
                </label>
                <textarea
                  name="focusPoints"
                  value={formData?.focusPoints}
                  onChange={handleInputChange}
                  placeholder="Key topics to cover (comma-separated)&#10;e.g., Pet hair removal, allergen filtration, odor control"
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  rows={4}
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Price Range
                </label>
                <Input
                  name="priceRange"
                  value={formData?.priceRange}
                  onChange={handleInputChange}
                  placeholder="e.g., $200-$500"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Seasonal Relevance
                </label>
                <Select
                  name="seasonalRelevance"
                  value={formData?.seasonalRelevance}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="">None</option>
                  <option value="spring">Spring</option>
                  <option value="summer">Summer</option>
                  <option value="fall">Fall</option>
                  <option value="winter">Winter</option>
                  <option value="holiday">Holiday Season</option>
                </Select>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <Button
                onClick={handleGenerate}
                disabled={loading || !formData?.title || !formData?.targetAudience}
                iconName="Sparkles"
                iconPosition="left"
                className="flex-1"
              >
                {loading ? 'Generating...' : 'Generate Guide'}
              </Button>
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={loading}
                iconName="RefreshCw"
              >
                Reset
              </Button>
            </div>
          </div>

          {/* Generated Content Preview */}
          <div className="bg-white rounded-xl shadow-subtle border border-border p-6">
            <h2 className="text-xl font-semibold text-text-primary mb-6 flex items-center gap-2">
              <Sparkles size={20} className="text-purple-600" />
              Generated Content
            </h2>

            {!generatedContent ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-muted/30 rounded-full flex items-center justify-center mb-4">
                  <FileText size={32} className="text-muted-foreground" />
                </div>
                <p className="text-text-secondary">
                  Fill in the parameters and click "Generate Guide" to create AI-powered content
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-text-primary mb-2">Title</h3>
                  <p className="text-text-secondary">{generatedContent?.title}</p>
                </div>

                <div>
                  <h3 className="font-semibold text-text-primary mb-2">Summary</h3>
                  <p className="text-text-secondary text-sm leading-relaxed">{generatedContent?.summary}</p>
                </div>

                <div>
                  <h3 className="font-semibold text-text-primary mb-2">Key Points</h3>
                  <ul className="space-y-2">
                    {generatedContent?.keyPoints?.map((point, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-text-secondary">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-text-primary mb-2">Content Preview</h3>
                  <div className="bg-muted/30 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <p className="text-sm text-text-secondary whitespace-pre-line">
                      {generatedContent?.content?.substring(0, 500)}...
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-text-primary mb-2">SEO Optimization</h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-text-secondary">
                      <span className="font-medium">Title:</span> {generatedContent?.seoTitle}
                    </p>
                    <p className="text-text-secondary">
                      <span className="font-medium">Description:</span> {generatedContent?.seoDescription}
                    </p>
                    <p className="text-text-secondary">
                      <span className="font-medium">Read Time:</span> {generatedContent?.readTimeMinutes} minutes
                    </p>
                  </div>
                </div>

                <Button
                  onClick={handleSave}
                  disabled={loading}
                  iconName="Save"
                  iconPosition="left"
                  fullWidth
                >
                  Save Buying Guide
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AIBuyingGuideGenerator;